import type { ControlSummary, Finding, Framework, ProductSummary } from './api.ts';

/**
 * Which slice of an assessment the reader is looking at: one Microsoft
 * product, one framework, one clause of it. Null at any level means all.
 *
 * Each level narrows the one after it — a clause only exists inside a
 * framework — so the three are resolved in order, and every option offered is
 * counted against the levels already chosen. An option with nothing under it is
 * never offered, which is what keeps a combination from arriving at an empty
 * page nobody can explain.
 */
export interface Scope {
  readonly product: string | null;
  readonly framework: string | null;
  readonly clause: string | null;
}

export const ALL: Scope = Object.freeze({ product: null, framework: null, clause: null });

export interface ScopeOption {
  readonly id: string;
  readonly label: string;
  readonly count: number;
}

export interface ScopeOptions {
  readonly products: readonly ScopeOption[];
  readonly frameworks: readonly ScopeOption[];
  readonly clauses: readonly ScopeOption[];
}

export interface ScopeCatalogue {
  readonly products: readonly ProductSummary[];
  readonly controls: readonly ControlSummary[];
  /** Already narrowed to what this client's report covers, where that is known. */
  readonly frameworks: readonly Framework[];
}

const CLAUSE_LABEL_MAX = 64;

/**
 * `A.5.14 — Information transfer` rather than the whole criterion. Titles run
 * to a paragraph and a select option is one line; the full text is what the
 * framework card and the report show.
 */
export const clauseLabel = (id: string, title: string): string => {
  const sentence = title.split(/\.\s/)[0]?.replace(/\.$/, '') ?? title;
  if (sentence.length <= CLAUSE_LABEL_MAX) return `${id} — ${sentence}`;
  const cut = sentence.slice(0, CLAUSE_LABEL_MAX);
  return `${id} — ${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:]$/, '')}…`;
};

const countWhere = (findings: readonly Finding[], test: (controlId: string) => boolean): number =>
  findings.filter((finding) => test(finding.controlId)).length;

const nonEmpty = (options: ScopeOption[]): ScopeOption[] =>
  options.filter((option) => option.count > 0);

interface Resolved {
  readonly options: ScopeOptions;
  readonly scope: Scope;
  readonly findings: readonly Finding[];
}

/**
 * The options at each level, the scope with any choice that no longer exists
 * dropped, and the findings inside it — in one pass, so the three cannot
 * disagree.
 *
 * Dropping rather than keeping a stale choice is deliberate. Pick Intune after
 * a CIS Microsoft 365 clause and there is nothing there: no Intune control maps
 * to that benchmark. Holding the clause would show an empty page with a
 * selection that is not in its own dropdown.
 */
export const resolveScope = (
  findings: readonly Finding[],
  catalogue: ScopeCatalogue,
  requested: Scope,
): Resolved => {
  const productsOf = new Map(catalogue.controls.map((c) => [c.id, c.products] as const));
  const inProduct = (product: string) => (controlId: string) =>
    (productsOf.get(controlId) ?? []).includes(product);

  const products = nonEmpty(
    catalogue.products.map((p) => ({ ...p, count: countWhere(findings, inProduct(p.id)) })),
  );
  const product = products.some((p) => p.id === requested.product) ? requested.product : null;
  const byProduct = product === null ? findings : findings.filter((f) => inProduct(product)(f.controlId));

  const inFramework = (framework: Framework) => (controlId: string) =>
    framework.criteria.some((criterion) => criterion.controlIds.includes(controlId));

  const frameworks = nonEmpty(
    catalogue.frameworks.map((f) => ({
      id: f.id,
      label: f.name,
      count: countWhere(byProduct, inFramework(f)),
    })),
  );
  const framework =
    frameworks.some((f) => f.id === requested.framework)
      ? (catalogue.frameworks.find((f) => f.id === requested.framework) ?? null)
      : null;
  const byFramework =
    framework === null ? byProduct : byProduct.filter((f) => inFramework(framework)(f.controlId));

  const clauses =
    framework === null
      ? []
      : nonEmpty(
          framework.criteria.map((criterion) => ({
            id: criterion.id,
            label: clauseLabel(criterion.id, criterion.title),
            count: countWhere(byFramework, (id) => criterion.controlIds.includes(id)),
          })),
        );
  const clause =
    framework === null
      ? null
      : (framework.criteria.find(
          (c) => c.id === requested.clause && clauses.some((o) => o.id === c.id),
        ) ?? null);
  const byClause =
    clause === null ? byFramework : byFramework.filter((f) => clause.controlIds.includes(f.controlId));

  return {
    options: { products, frameworks, clauses },
    scope: { product, framework: framework?.id ?? null, clause: clause?.id ?? null },
    findings: byClause,
  };
};

export const isNarrowed = (scope: Scope): boolean =>
  scope.product !== null || scope.framework !== null || scope.clause !== null;

/** `Exchange Online · ISO/IEC 27001:2022 — Annex A · A.5.14`, for the line above the tiles. */
export const describeScope = (scope: Scope, options: ScopeOptions): string =>
  [
    options.products.find((o) => o.id === scope.product)?.label,
    options.frameworks.find((o) => o.id === scope.framework)?.label,
    scope.clause ?? undefined,
  ]
    .filter((part) => part !== undefined)
    .join(' · ');

/**
 * The framework criteria to show against a finding.
 *
 * Only the frameworks this client's report covers. An ISO criterion printed
 * beside a control in a SOC 2-only client's console reads as a claim about a
 * framework nobody is assessing them against, and the console and the report
 * they are handed should not disagree about that.
 *
 * An empty or absent list means *unknown*, not *none*, and shows everything.
 * The tenant screen fills this in after two further calls — the framework
 * catalogue and the account's package — and a client whose labels vanished for
 * a moment on every page load would look like a client who is covered for
 * nothing. A client genuinely covered for nothing has no assessment to read.
 */
export const visibleMappings = <T extends { readonly framework: string }>(
  mappings: readonly T[],
  frameworks: readonly { readonly id: string }[] | undefined,
): readonly T[] => {
  if (frameworks === undefined || frameworks.length === 0) return mappings;
  const covered = new Set(frameworks.map((framework) => framework.id));
  return mappings.filter((mapping) => covered.has(mapping.framework));
};
